import React, { useEffect, useRef, useState } from "react";
import {
  HeartStraightIcon,
  CurrencyDollarIcon,
  LightningIcon,
  DiamondIcon,
  CrownSimpleIcon,
} from "@phosphor-icons/react";

/**
 * Tiered "Super Tip" toast system. Bigger tips get more dramatic UI —
 * longer display time, richer gradient, pulse animation, crown/diamond
 * iconography. Incentivizes larger tips (Twitch super-chat style).
 *
 * Tiers:
 *   Standard  ($1–$19)   — emerald card, 4.2s
 *   Lightning ($20–$49)  — amber-orange card with ⚡, 6s
 *   Diamond   ($50–$99)  — sky-to-violet card with 💎 + pulse, 8s
 *   Super     ($100+)    — rainbow-gradient card with 👑 + pulse + sparkle, 12s
 */

const TIERS = [
  { min: 100, ms: 12000, label: "SUPER TIP", icon: CrownSimpleIcon, cls: "tip-tier-super" },
  { min: 50, ms: 8000, label: "DIAMOND TIP", icon: DiamondIcon, cls: "tip-tier-diamond" },
  { min: 20, ms: 6000, label: "LIGHTNING TIP", icon: LightningIcon, cls: "tip-tier-lightning" },
  { min: 1, ms: 4200, label: null, icon: HeartStraightIcon, cls: "tip-tier-standard" },
];

function tierFor(amount) {
  return TIERS.find((t) => amount >= t.min) || TIERS[TIERS.length - 1];
}

export function TipToast({ tip, onDone }) {
  useEffect(() => {
    if (!tip) return;
    const amt = Number(tip.amount || 0);
    const t = setTimeout(onDone, tierFor(amt).ms);
    return () => clearTimeout(t);
  }, [tip?.id, onDone, tip]);

  if (!tip) return null;
  const amt = Number(tip.amount || 0);
  const tier = tierFor(amt);
  const TierIcon = tier.icon;
  const gradients = {
    "tip-tier-standard": "from-emerald-500 to-green-600",
    "tip-tier-lightning": "from-amber-400 to-orange-500",
    "tip-tier-diamond": "from-sky-400 via-indigo-500 to-violet-600",
    "tip-tier-super":
      "from-pink-500 via-amber-400 via-yellow-400 to-indigo-500",
  };

  return (
    <div
      className={`fixed left-1/2 top-24 -translate-x-1/2 z-[60] pointer-events-none ${
        tier.cls !== "tip-tier-standard" ? "animate-pulse" : ""
      }`}
      data-testid={`tip-toast-${tip.id}`}
    >
      <div
        className={`tip-toast-card rounded-2xl bg-gradient-to-r ${gradients[tier.cls]} text-white px-5 py-3 shadow-2xl shadow-black/40 ring-2 ring-white/20 max-w-md`}
        data-testid={`tip-toast-tier-${tier.cls}`}
      >
        {tier.label && (
          <div className="inline-flex items-center gap-1 bg-white/20 text-white rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest mb-2">
            <TierIcon size={10} weight="fill" />
            {tier.label}
          </div>
        )}
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-white/20 grid place-items-center flex-shrink-0">
            <TierIcon size={22} weight="fill" />
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="font-display font-black text-lg truncate">
                {tip.tipper_name || "Someone"}
              </span>
              <span className="text-xs opacity-90">tipped</span>
              <span className="font-display font-black text-2xl inline-flex items-center">
                <CurrencyDollarIcon size={18} weight="bold" />
                {amt.toFixed(amt >= 10 ? 0 : 2)}
              </span>
            </div>
            {tip.message ? (
              <p className="text-sm text-white/95 italic mt-1 line-clamp-2">
                "{tip.message}"
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TipToastQueue({ incoming }) {
  const [current, setCurrent] = useState(null);
  const queueRef = useRef([]);

  useEffect(() => {
    if (incoming) {
      queueRef.current.push(incoming);
      if (!current) {
        setCurrent(queueRef.current.shift());
      }
    }
    // eslint-disable-next-line
  }, [incoming]);

  const advance = () => {
    const next = queueRef.current.shift();
    setCurrent(next || null);
  };

  if (!current) return null;
  return <TipToast tip={current} onDone={advance} />;
}
